import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  Heading, LinkAsButton, Collapsible,
} from '@printdeal/ui-library';
import { dispatchDataLayerEntry } from '../../../helpers/dataLayerHelpers/productDataLayerHelper';
import Content from '../../widgets/Content';
import type { ILinkedFAQ } from '../../../types/product/productDetailPage';

interface Props {
  linkedFaqs: ILinkedFAQ[];
  contentful_id: string;
  productName: string;
  variantSku?: string;
  pageName: string;
}

const LinkedFaqs = ({
  linkedFaqs,
  contentful_id,
  productName,
  variantSku,
  pageName,
}: Props) => {
  const [showMore, setShowMore] = useState(false);
  const VISIBLE_CONTENT = 3;
  const SHOW_MORE_MAX = linkedFaqs.length;

  const handleShowMoreFaqClick = () => {
    setShowMore(!showMore);
    dispatchDataLayerEntry(
      pageName,
      'click_faq_show_more',
      {
        productName,
        contentfulId: contentful_id,
        ...(variantSku && { variantSku }),
      },
    );
  };

  const handleFaqClick = ({ title, open }: { open: boolean, title: string }) => {
    dispatchDataLayerEntry(
      pageName,
      'click_faq',
      {
        productName,
        contentfulId: contentful_id,
        ...(variantSku && { variantSku }),
        faqName: title,
        open,
      },
    );
  };

  const visibleRelatedContent = linkedFaqs.slice(0, VISIBLE_CONTENT);
  const seeMoreRelatedContent = linkedFaqs.slice(VISIBLE_CONTENT, SHOW_MORE_MAX);

  return (
    <>
      <Heading variant="display" as="h2">
        <FormattedMessage id="support.faq.title" defaultMessage="Frequently Asked Questions" />
      </Heading>
      {visibleRelatedContent.map(({ id, title, content }) =>
        (<Content key={id} title={title} content={content} handleClick={handleFaqClick} />))}
      {seeMoreRelatedContent.length > 0 && (
      <div className="pd-mb-5">
        <Collapsible variant="secondary" id="relatedContent" isOpen={showMore}>
          <Collapsible.Panel unmount={false}>
            {seeMoreRelatedContent.map(({ id, title, content }) =>
              (<Content key={id} title={title} content={content} handleClick={handleFaqClick} />))}
          </Collapsible.Panel>
        </Collapsible>
        <LinkAsButton variant="inherit" iconAfter={`double-chevron-${showMore ? 'up' : 'down'}`} onClick={() => handleShowMoreFaqClick()}>
          {showMore ? (
            <FormattedMessage
              id="helpcenter.related.content.see.less"
              defaultMessage="Show less questions"
              values={{ count: seeMoreRelatedContent.length }}
            />
          ) : (
            <FormattedMessage
              id="helpcenter.related.content.see.more"
              defaultMessage="Show more questions ({count})"
              values={{ count: seeMoreRelatedContent.length }}
            />
          )}
        </LinkAsButton>
      </div>
      )}
    </>
  );
};

export default LinkedFaqs;
